// LaCuentaScreen.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    SpeedDial,
    SpeedDialAction,
    Stack,
    TextField,
    Typography,
    Badge,
    Snackbar,
    Alert,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import BackspaceIcon from "@mui/icons-material/Backspace";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import HistoryIcon from "@mui/icons-material/History";
import UndoIcon from "@mui/icons-material/Undo";

const DINERO_BASE = {
    // According to player count, each starts with different money
    2: 800,
    3: 900,
    4: 1000,
    5: 1100,
    6: 1200,
    7: 1300,
    8: 1400,
};

const LS_KEY = "la_cuenta_personas";

const loadPersonasFromStorage = () => {
    // NOTE: keep it safe
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        return parsed;
    } catch {
        return null;
    }
};

const getBaseMoney = (count) => DINERO_BASE[count] ?? 1400;

const endsWithOp = (s) => s.endsWith("+") || s.endsWith("-");

const computeValor = (raw) => {
    if (!raw) return 0;

    // Remove trailing operators so we can evaluate safely
    let s = raw;
    while (s.length && endsWithOp(s)) s = s.slice(0, -1);
    if (!s) return 0;

    let total = 0;
    let num = "";
    let sign = 1;

    for (let i = 0; i < s.length; i++) {
        const ch = s[i];

        if (ch >= "0" && ch <= "9") {
            num += ch;
            continue;
        }

        if (ch === "+" || ch === "-") {
            if (num !== "") {
                total += sign * parseInt(num, 10);
                num = "";
            }
            sign = ch === "+" ? 1 : -1;
        }
    }

    if (num !== "") total += sign * parseInt(num, 10);
    return total;
};

const formatMoney = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return "0";
    if (Number.isInteger(num)) return String(num);

    // keep 2 decimals max, trim trailing zeros
    return num
        .toFixed(2)
        .replace(/\.00$/, "")
        .replace(/(\.\d)0$/, "$1");
};

const LaCuentaScreen = () => {
    const [cuenta, setCuenta] = useState(""); // e.g. "30+10+5-70-10"
    const [valor, setValor] = useState(0); // numeric value of "cuenta"

    const [pagosOpen, setPagosOpen] = useState(false);
    const [pagos, setPagos] = useState([]);

    const [personas, setPersonas] = useState(() => {
        const saved = loadPersonasFromStorage();
        if (saved) return saved;

        // NOTE: default fallback
        return [
            { id: "p1", name: "Ale", amount: 800 },
            { id: "p2", name: "Pablo", amount: 800 },
            { id: "p3", name: "Jorge", amount: 800 },
        ];
    });

    useEffect(() => {
        // NOTE: persist scores
        try {
            localStorage.setItem(LS_KEY, JSON.stringify(personas));
        } catch {
            // NOTE: ignore storage errors
        }
    }, [personas]);

    const [seleccionadas, setSeleccionadas] = useState([]);

    // Toast
    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "warning",
    });

    const showToast = (message, severity = "warning") => {
        setToast({ open: true, message, severity });
    };

    const closeToast = (_event, reason) => {
        if (reason === "clickaway") return;
        setToast((prev) => ({ ...prev, open: false }));
    };

    // Edit players UI state
    const [editOpen, setEditOpen] = useState(false);
    const [editPersonas, setEditPersonas] = useState([]);

    const baseMoney = useMemo(
        () => getBaseMoney(personas.length),
        [personas.length]
    );

    const setCuentaAndValor = (next) => {
        setCuenta(next);
        setValor(computeValor(next));
    };

    const toggleSeleccion = (id) => {
        setSeleccionadas((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const onDigit = (d) => {
        // If the user just paid/reset and wants to start again, they can type immediately
        setCuentaAndValor(`${cuenta}${d}`);
    };

    const onOp = (op) => {
        if (!cuenta) {
            // Allow starting with "-"
            if (op === "-") setCuentaAndValor("-");
            return;
        }

        if (endsWithOp(cuenta)) {
            // Replace last operator
            setCuentaAndValor(`${cuenta.slice(0, -1)}${op}`);
            return;
        }

        setCuentaAndValor(`${cuenta}${op}`);
    };

    const onBackspace = () => {
        if (!cuenta) return;
        setCuentaAndValor(cuenta.slice(0, -1));
    };

    const onRevertLastPago = () => {
        if (!pagos.length) {
            showToast("No hay pagos para revertir.", "info");
            return;
        }

        const last = pagos[0];
        const byId = new Map((last.changes || []).map((c) => [c.id, c.before]));

        setPersonas((prev) =>
            prev.map((p) =>
                byId.has(p.id) ? { ...p, amount: byId.get(p.id) } : p
            )
        );

        setPagos((prev) => prev.slice(1));
        showToast("Último pago revertido.", "success");
    };
    const onPagar = () => {
        // If there is no player selected, show toast
        if (!seleccionadas.length) {
            showToast("Debes seleccionar al menos un jugador.", "warning");
            return;
        }

        const toPay = computeValor(cuenta); // total amount to split
        if (!toPay) return;

        const selectedCount = seleccionadas.length;
        const share = Math.round((toPay / selectedCount) * 100) / 100;

        // Build change lines based on CURRENT amounts (before state update)
        const changes = personas
            .filter((p) => seleccionadas.includes(p.id))
            .map((p) => ({
                id: p.id, // add (needed for revert)
                name: p.name,
                before: p.amount,
                after: p.amount - share,
            }));

        setPersonas((prev) =>
            prev.map((p) =>
                seleccionadas.includes(p.id)
                    ? { ...p, amount: p.amount - share }
                    : p
            )
        );

        const entry = {
            id: `pay_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
            total: toPay,
            share,
            changes,
        };
        setPagos((prev) => [entry, ...prev]);

        setCuentaAndValor("");
        setSeleccionadas([]);
    };

    const onResetear = () => {
        const nextBase = getBaseMoney(personas.length);
        setPersonas((prev) => prev.map((p) => ({ ...p, amount: nextBase })));
        setCuentaAndValor("");
        setSeleccionadas([]);
        setPagos([]);
    };

    const openEditar = () => {
        setEditPersonas(personas.map((p) => ({ ...p })));
        setEditOpen(true);
    };

    const closeEditar = () => {
        setEditOpen(false);
        setEditPersonas([]);
    };

    const saveEditar = () => {
        const cleaned = editPersonas
            .map((p) => ({
                ...p,
                name: (p.name ?? "").trim() || "Jugador",
            }))
            .filter(
                (p, idx, arr) => arr.findIndex((x) => x.id === p.id) === idx
            );

        setPersonas(cleaned);

        // Remove selections that no longer exist
        const allowed = new Set(cleaned.map((p) => p.id));
        setSeleccionadas((prev) => prev.filter((id) => allowed.has(id)));

        closeEditar();
    };

    const addJugador = () => {
        const newId = `p_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        setEditPersonas((prev) => [
            ...prev,
            { id: newId, name: "Nuevo", amount: baseMoney },
        ]);
    };

    const deleteJugador = (id) => {
        setEditPersonas((prev) => prev.filter((p) => p.id !== id));
    };

    const updateJugadorName = (id, name) => {
        setEditPersonas((prev) =>
            prev.map((p) => (p.id === id ? { ...p, name } : p))
        );
    };

    const keypad = [
        { label: "7", onClick: () => onDigit("7"), xs: 3, variant: "outlined" },
        { label: "8", onClick: () => onDigit("8"), xs: 3, variant: "outlined" },
        { label: "9", onClick: () => onDigit("9"), xs: 3, variant: "outlined" },
        {
            label: "+",
            onClick: () => onOp("+"),
            xs: 3,
            variant: "contained",
            icon: <AddIcon fontSize="inherit" />,
        },

        { label: "4", onClick: () => onDigit("4"), xs: 3, variant: "outlined" },
        { label: "5", onClick: () => onDigit("5"), xs: 3, variant: "outlined" },
        { label: "6", onClick: () => onDigit("6"), xs: 3, variant: "outlined" },
        {
            label: "-",
            onClick: () => onOp("-"),
            xs: 3,
            variant: "contained",
            icon: <RemoveIcon fontSize="inherit" />,
        },

        { label: "1", onClick: () => onDigit("1"), xs: 3, variant: "outlined" },
        { label: "2", onClick: () => onDigit("2"), xs: 3, variant: "outlined" },
        { label: "3", onClick: () => onDigit("3"), xs: 3, variant: "outlined" },
        {
            label: "retroceso",
            onClick: onBackspace,
            xs: 3,
            variant: "contained",
            icon: <BackspaceIcon fontSize="inherit" />,
        },

        { label: "0", onClick: () => onDigit("0"), xs: 6, variant: "outlined" },
        { label: "Pagar", onClick: onPagar, xs: 6, variant: "contained" },
    ];

    return (
        <Container
            maxWidth={false}
            disableGutters
            sx={{ height: "100vh", position: "relative" }}
        >
            <Stack direction="column" sx={{ height: "100%", width: "100%" }}>
                {/* Players on top */}
                <Box sx={{ p: 2, flex: "0 0 auto" }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                    >
                        <Typography
                            variant="body1"
                            // sx={{ opacity: 0.7 }}
                            noWrap
                        >
                            La Cuenta
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ opacity: 0.7 }}
                            noWrap
                        >
                            Base: {baseMoney}
                        </Typography>
                        {/* <PersonIcon />
                        <Typography variant="h6" noWrap>
                            Jugadores
                        </Typography> */}

                        <Box sx={{ flex: 1 }} />

                        <IconButton
                            onClick={onRevertLastPago}
                            size="small"
                            aria-label="Revertir último pago"
                            disabled={!pagos.length}
                        >
                            <UndoIcon />
                        </IconButton>

                        <IconButton
                            onClick={() => setPagosOpen(true)}
                            size="small"
                            aria-label="Historial de pagos"
                        >
                            <Badge badgeContent={pagos.length} color="primary">
                                <HistoryIcon />
                            </Badge>
                        </IconButton>

                        <IconButton onClick={openEditar} size="small">
                            <GroupAddIcon />
                        </IconButton>

                        <IconButton onClick={onResetear} size="small">
                            <RestartAltIcon />
                        </IconButton>
                    </Stack>

                    <Paper
                        variant="outlined"
                        sx={{ maxHeight: "35vh", overflow: "auto", p: 1 }}
                    >
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {(() => {
                                const maxAmount = personas.length
                                    ? Math.max(...personas.map((x) => x.amount))
                                    : 0;

                                return personas.map((p) => {
                                    const checked = seleccionadas.includes(
                                        p.id
                                    );
                                    const isTop = p.amount === maxAmount;
                                    const isNeg = p.amount <= 0;

                                    return (
                                        <Button
                                            key={p.id}
                                            onClick={() =>
                                                toggleSeleccion(p.id)
                                            }
                                            variant={
                                                checked
                                                    ? "contained"
                                                    : "outlined"
                                            }
                                            color={
                                                isTop ? "success" : "primary"
                                            } // green button for top (ties included)
                                            sx={{
                                                textTransform: "none",
                                                px: 1.5,
                                                py: 1,
                                                borderRadius: 2,
                                                minWidth: 0, // content width
                                                borderColor: isNeg
                                                    ? "error.main"
                                                    : undefined,
                                                ...(isNeg && !checked
                                                    ? { color: "error.main" } // outlined negative: red text + red border
                                                    : {}),
                                                ...(isNeg && checked
                                                    ? {
                                                          bgcolor: "error.main",
                                                          "&:hover": {
                                                              bgcolor:
                                                                  "error.dark",
                                                          },
                                                      } // contained negative: red button
                                                    : {}),
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    lineHeight: 1.05,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 18,
                                                        fontWeight: 700,
                                                    }}
                                                    noWrap
                                                >
                                                    {p.name}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 16,
                                                        fontWeight: 700,
                                                        opacity: 0.9,
                                                    }}
                                                    noWrap
                                                >
                                                    {p.amount}
                                                </Typography>
                                            </Box>
                                        </Button>
                                    );
                                });
                            })()}
                        </Box>
                    </Paper>
                </Box>

                <Divider />

                {/* Calculator below */}
                <Box
                    sx={{
                        p: 2,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            Cuenta
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{ wordBreak: "break-word", minHeight: 32 }}
                        >
                            {cuenta || "—"}
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Stack
                            direction="row"
                            alignItems="baseline"
                            spacing={1}
                        >
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                Total
                            </Typography>
                            <Typography variant="h4">{valor}</Typography>
                        </Stack>
                    </Paper>

                    <Paper
                        variant="outlined"
                        sx={{ p: 2, flex: 1, minHeight: 0, display: "flex" }}
                    >
                        <Grid container spacing={1} sx={{ width: "100%" }}>
                            {keypad.map((k, idx) => (
                                <Grid size={k.xs} key={`${k.label}_${idx}`}>
                                    <Button
                                        fullWidth
                                        size="large"
                                        variant={k.variant}
                                        sx={{
                                            minHeight: 64,
                                            height: "100%",
                                            fontSize: k.icon
                                                ? "clamp(28px, 6vw, 70px)"
                                                : "clamp(14px, 5vw, 50px)",
                                        }}
                                        onClick={k.onClick}
                                        disabled={k.label === "Pagar" && !valor}
                                    >
                                        {k.icon ? k.icon : k.label}
                                    </Button>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Box>
                <Divider />
            </Stack>

            {/* Edit players dialog */}
            <Dialog
                open={editOpen}
                onClose={closeEditar}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{ display: "flex", alignItems: "center", pr: 1 }}
                >
                    <Typography variant="h6" sx={{ flex: 1 }}>
                        Editar jugadores
                    </Typography>

                    <IconButton
                        onClick={closeEditar}
                        aria-label="Cerrar"
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={1.25}>
                        {editPersonas.map((p, idx) => (
                            <Stack
                                key={p.id}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={p.name}
                                    onChange={(e) =>
                                        updateJugadorName(p.id, e.target.value)
                                    }
                                />
                                <IconButton
                                    aria-label="Borrar"
                                    onClick={() => deleteJugador(p.id)}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))}

                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addJugador}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            Agregar jugador
                        </Button>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeEditar}>Cancelar</Button>
                    <Button variant="contained" onClick={saveEditar}>
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={pagosOpen}
                onClose={() => setPagosOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                    <HistoryIcon />
                    Historial de pagos
                    <Box sx={{ flex: 1 }} />
                    <IconButton
                        onClick={() => setPagosOpen(false)}
                        aria-label="Cerrar"
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {pagos.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            No hay pagos desde el último reset.
                        </Typography>
                    ) : (
                        <Paper variant="outlined">
                            <List dense>
                                {pagos.map((p) => {
                                    const totalDelta = -p.total;

                                    const left = `${formatMoney(totalDelta)}:`; // what goes in bold (includes :)
                                    const right = (p.changes || [])
                                        .map(
                                            (c) =>
                                                `${c.name} (${formatMoney(
                                                    c.before
                                                )} → ${formatMoney(c.after)})`
                                        )
                                        .join(", ");

                                    return (
                                        <ListItem key={p.id}>
                                            <ListItemText
                                                primary={
                                                    <span>
                                                        <strong>{left}</strong>{" "}
                                                        {right}
                                                    </span>
                                                }
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Paper>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setPagosOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Toast */}
            <Snackbar
                open={toast.open}
                autoHideDuration={2500}
                onClose={closeToast}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={closeToast}
                    severity={toast.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default LaCuentaScreen;
